import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldX,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type VerificationStatus =
  | 'verified'
  | 'invalid_signature'
  | 'not_configured'
  | 'unmapped'
  | 'missing_signature'
  | 'malformed';

type HandlerStatus = 'processed' | 'duplicate' | 'ignored' | 'handler_error' | 'skipped';

interface WebhookDelivery {
  _id: string;
  gateway: 'stripe' | 'razorpay';
  eventId: string;
  eventType: string;
  receivedAt: string;
  rawBody: string;
  signatureHeader?: string;
  verificationStatus: VerificationStatus;
  verifiedMode?: 'live' | 'test';
  orderMode?: 'live' | 'test';
  handlerStatus?: HandlerStatus;
  handlerError?: string;
  resolvedVia?: 'metadata' | 'gateway_payment_id';
  gatewayPaymentId?: string;
  gatewayOrderId?: string;
  httpStatus: number;
}

interface Props {
  projectId: string;
  orderId: string;
  gateway?: 'stripe' | 'razorpay' | string;
  gatewayPaymentId?: string;
  /** Current order status — used to decide whether to surface the "desynced" banner. */
  orderStatus?: string;
  /** Payment status — same purpose. */
  paymentStatus?: string;
}

// Deep-link to the gateway dashboard for a payment.
function gatewayDashboardUrl(
  gateway: Props['gateway'],
  gatewayPaymentId: string | undefined,
  mode?: 'live' | 'test'
): string | undefined {
  if (!gatewayPaymentId) return undefined;
  if (gateway === 'razorpay') {
    // Razorpay uses the same URL for live and test — test is selected via the dashboard toggle.
    return `https://dashboard.razorpay.com/app/payments/${gatewayPaymentId}`;
  }
  if (gateway === 'stripe') {
    const prefix = mode === 'test' ? '/test' : '';
    return `https://dashboard.stripe.com${prefix}/payments/${gatewayPaymentId}`;
  }
  return undefined;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(d);
}

function verificationBadge(status: VerificationStatus) {
  const map: Record<
    VerificationStatus,
    { label: string; className: string; Icon: typeof ShieldCheck }
  > = {
    verified: {
      label: 'Verified',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      Icon: ShieldCheck,
    },
    invalid_signature: {
      label: 'Invalid signature',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      Icon: ShieldX,
    },
    not_configured: {
      label: 'Secret missing',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      Icon: ShieldOff,
    },
    unmapped: {
      label: 'Unmatched',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      Icon: ShieldAlert,
    },
    missing_signature: {
      label: 'No signature',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      Icon: ShieldOff,
    },
    malformed: {
      label: 'Malformed',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      Icon: ShieldAlert,
    },
  };
  const entry = map[status];
  const Icon = entry.Icon;
  return (
    <Badge className={`${entry.className} gap-1`} variant="outline">
      <Icon className="h-3 w-3" />
      {entry.label}
    </Badge>
  );
}

function handlerBadge(status?: HandlerStatus) {
  if (!status || status === 'skipped') return null;
  const map: Record<Exclude<HandlerStatus, 'skipped'>, { label: string; className: string }> = {
    processed: { label: 'Processed', className: 'bg-blue-100 text-blue-800' },
    duplicate: { label: 'Duplicate', className: 'bg-gray-100 text-gray-800' },
    ignored: { label: 'Ignored', className: 'bg-gray-100 text-gray-800' },
    handler_error: { label: 'Handler error', className: 'bg-red-100 text-red-800' },
  };
  const entry = map[status as Exclude<HandlerStatus, 'skipped'>];
  if (!entry) return null;
  return (
    <Badge className={entry.className} variant="outline">
      {entry.label}
    </Badge>
  );
}

function prettyJSON(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function PaymentEventsSection({
  projectId,
  orderId,
  gateway,
  gatewayPaymentId,
  orderStatus,
  paymentStatus,
}: Props) {
  const queryClient = useQueryClient();
  const [loadEnabled, setLoadEnabled] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [fetchingSnapshot, setFetchingSnapshot] = useState(false);
  const [snapshot, setSnapshot] = useState<any>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['order-gateway-events', projectId, orderId],
    queryFn: async () => {
      const res = await api.get(
        `/projects/${projectId}/ecommerce/orders/${orderId}/gateway-events`
      );
      return res.data as { success: boolean; data: WebhookDelivery[] };
    },
    enabled: loadEnabled && Boolean(projectId) && Boolean(orderId),
    staleTime: 30_000,
  });

  const deliveries = data?.data ?? [];

  // Heuristic: if the delivery list shows a capture event but the order isn't
  // confirmed, we had a state-transition bug and a reconcile will fix it.
  // Uses the deliveries we already fetched to drive UI — pragmatic rather than
  // a separate server-side check.
  const hasCaptureEvent = useMemo(
    () =>
      deliveries.some(
        (d) =>
          (d.eventType === 'payment.captured' || d.eventType === 'payment_intent.succeeded') &&
          d.verificationStatus === 'verified'
      ),
    [deliveries]
  );
  const looksDesynced =
    hasCaptureEvent &&
    orderStatus !== 'confirmed' &&
    orderStatus !== 'processing' &&
    orderStatus !== 'shipped' &&
    orderStatus !== 'delivered' &&
    orderStatus !== 'refunded' &&
    paymentStatus !== 'paid';

  const reconcileMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/projects/${projectId}/ecommerce/orders/${orderId}/reconcile`);
      return res.data as {
        success: boolean;
        data:
          | { action: 'noop'; reason: string }
          | {
              action: 'confirmed';
              gateway: string;
              gatewayPaymentId: string;
              amount: number;
            }
          | { action: 'refunded'; refundId: string; isFullRefund: boolean };
      };
    },
    onSuccess: (payload) => {
      queryClient.invalidateQueries({ queryKey: ['order', projectId, orderId] });
      queryClient.invalidateQueries({
        queryKey: ['order-gateway-events', projectId, orderId],
      });
      queryClient.invalidateQueries({ queryKey: ['orders', projectId] });
      if (payload.data.action === 'confirmed') {
        toast.success(
          `Order reconciled — confirmed from ${payload.data.gateway} ${payload.data.gatewayPaymentId}`
        );
      } else if (payload.data.action === 'refunded') {
        toast.success(`Order reconciled — refund ${payload.data.refundId} applied`);
      } else {
        toast.info(`Nothing to reconcile (${payload.data.reason}).`);
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Reconcile failed');
    },
  });

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFetchSnapshot = async () => {
    setSnapshotOpen(true);
    setFetchingSnapshot(true);
    setSnapshot(null);
    setSnapshotError(null);
    try {
      const res = await api.get(
        `/projects/${projectId}/ecommerce/orders/${orderId}/gateway-snapshot`
      );
      setSnapshot(res.data?.data ?? null);
      if (!res.data?.success) {
        setSnapshotError(res.data?.error?.message || 'Failed to fetch from gateway');
      }
    } catch (err: any) {
      setSnapshotError(
        err?.response?.data?.error?.message || err?.message || 'Failed to fetch from gateway'
      );
    } finally {
      setFetchingSnapshot(false);
    }
  };

  // Best-effort deep-link — we don't know the mode here, Razorpay link is mode-agnostic
  // and Stripe link falls back to live.
  const dashboardUrl = gatewayDashboardUrl(gateway, gatewayPaymentId);

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4" />
              Payment Events
            </CardTitle>
            {loadEnabled && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label="Refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!loadEnabled && (
            <div className="text-muted-foreground text-xs">
              Every Razorpay/Stripe webhook delivery attached to this order is archived here —
              including ones that failed signature verification or weren't acted on. Useful when an
              order looks stuck.
            </div>
          )}

          {looksDesynced && (
            <div className="space-y-2 rounded-md border border-red-300 bg-red-50 p-3 text-xs dark:border-red-900 dark:bg-red-950/40">
              <div className="font-medium text-red-900 dark:text-red-100">
                Order looks out of sync with the gateway
              </div>
              <p className="text-red-900/80 dark:text-red-200/80">
                A verified <code>payment captured</code> webhook is on record for this order, but
                the order is not marked confirmed. The customer was likely charged. Click Reconcile
                to replay the archived events and repair the order state.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => reconcileMutation.mutate()}
                disabled={reconcileMutation.isPending}
                className="border-red-400 text-red-900 dark:text-red-100"
              >
                {reconcileMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wrench className="mr-1.5 h-3.5 w-3.5" />
                )}
                Reconcile from events
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {!loadEnabled ? (
              <Button size="sm" variant="outline" onClick={() => setLoadEnabled(true)}>
                Show webhook deliveries
              </Button>
            ) : null}
            {gateway && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleFetchSnapshot}
                disabled={!gatewayPaymentId}
                title={
                  !gatewayPaymentId
                    ? 'Order has no gateway payment id yet'
                    : `Fetch the live record from ${gateway}`
                }
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Fetch from {gateway === 'razorpay' ? 'Razorpay' : 'Stripe'}
              </Button>
            )}
            {dashboardUrl && (
              <a
                href={dashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <Button size="sm" variant="outline">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                  Open in {gateway === 'razorpay' ? 'Razorpay' : 'Stripe'} dashboard
                </Button>
              </a>
            )}
          </div>

          {loadEnabled && isLoading && (
            <div className="text-muted-foreground flex items-center gap-2 py-2 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading deliveries…
            </div>
          )}

          {loadEnabled && !isLoading && deliveries.length === 0 && (
            <div className="border-muted-foreground/20 text-muted-foreground rounded border border-dashed p-3 text-xs">
              No webhook deliveries on record for this order. If the payment succeeded in the
              gateway's dashboard, the webhook never reached Tyashin — double-check the webhook URL
              + secret in Settings → Payments, then retry the payment.
            </div>
          )}

          {loadEnabled && deliveries.length > 0 && (
            <ul className="divide-y rounded-md border">
              {deliveries.map((d) => {
                const isOpen = expanded.has(d._id);
                return (
                  <li key={d._id} className="text-xs">
                    <button
                      type="button"
                      onClick={() => toggle(d._id)}
                      className="hover:bg-muted/50 flex w-full items-start gap-2 p-2 text-left"
                    >
                      {isOpen ? (
                        <ChevronDown className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <ChevronRight className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-medium">{d.eventType}</span>
                          {verificationBadge(d.verificationStatus)}
                          {handlerBadge(d.handlerStatus)}
                          {d.verifiedMode && (
                            <Badge variant="outline" className="font-normal">
                              {d.verifiedMode} mode
                            </Badge>
                          )}
                        </div>
                        <div className="text-muted-foreground">
                          {formatDateTime(d.receivedAt)} · HTTP {d.httpStatus} · {d.gateway}
                          {d.eventId.startsWith('anon_') ? '' : ` · ${d.eventId}`}
                        </div>
                        {d.handlerError && (
                          <div className="text-red-600 dark:text-red-400">
                            Handler error: {d.handlerError}
                          </div>
                        )}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="space-y-2 px-2 pb-2">
                        {(d.verificationStatus !== 'verified' ||
                          d.handlerStatus === 'handler_error') && (
                          <DeliveryRemediation status={d.verificationStatus} />
                        )}
                        <pre className="bg-muted/40 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded p-2 font-mono text-[10px] leading-snug">
                          {prettyJSON(d.rawBody)}
                        </pre>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={snapshotOpen} onOpenChange={setSnapshotOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Live snapshot — {gateway === 'razorpay' ? 'Razorpay' : 'Stripe'}
            </DialogTitle>
            <DialogDescription>
              Fetched directly from the gateway just now. Compare against what Tyashin stored on the
              order to spot missed webhooks.
            </DialogDescription>
          </DialogHeader>
          {fetchingSnapshot ? (
            <div className="flex items-center gap-2 py-6 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Fetching…
            </div>
          ) : snapshotError ? (
            <div className="text-sm text-red-600 dark:text-red-400">{snapshotError}</div>
          ) : snapshot ? (
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-xs">
                <span>Mode: {snapshot.mode}</span>
                <span>·</span>
                <span>HTTP {snapshot.httpStatus}</span>
                <span>·</span>
                <span>
                  {snapshot.found ? (
                    <span className="inline-flex items-center gap-1 text-green-700">
                      <CheckCircle className="h-3 w-3" /> Found
                    </span>
                  ) : (
                    'Not found'
                  )}
                </span>
                <span>·</span>
                <span>Fetched {formatDateTime(snapshot.fetchedAt)}</span>
              </div>
              <pre className="bg-muted/40 max-h-96 overflow-auto whitespace-pre-wrap break-all rounded p-2 font-mono text-[10px] leading-snug">
                {JSON.stringify(snapshot.data, null, 2)}
              </pre>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DeliveryRemediation({ status }: { status: VerificationStatus }) {
  const messages: Record<VerificationStatus, string | null> = {
    verified: null,
    invalid_signature:
      'The signature header did not match either the live or test webhook secret on file. Check Settings → Payments for a secret mismatch.',
    not_configured:
      'No webhook secret was configured for this project when the delivery arrived. Add it in Settings → Payments and re-fire the webhook from the gateway dashboard.',
    unmapped:
      'The payload had no reference to a Tyashin order (no notes / metadata). Commonly caused by triggering a test payment directly from the gateway dashboard instead of through the storefront.',
    missing_signature:
      'The request arrived without a signature header. Either something is proxying out the header, or the request is not from the gateway.',
    malformed:
      'The body was not valid JSON. A broken proxy between the gateway and Tyashin is the usual culprit.',
  };
  const msg = messages[status];
  if (!msg) return null;
  return (
    <div className="rounded border-l-2 border-amber-400 bg-amber-50 p-2 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      {msg}
    </div>
  );
}
